import axios from "axios";
import { toast } from "sonner";
import { supabase } from "./supabase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Refresh a little before expiry so a mobile/PWA that was suspended (e.g. while the
// camera/NFC scanner was open) doesn't send an already-expired token and get a 401.
const PROACTIVE_REFRESH_SECONDS = 60;

// One shared refresh across all concurrent requests (Supabase rotates refresh tokens,
// so parallel refreshSession() calls would race and invalidate each other).
let refreshPromise = null;
let redirectingToLogin = false;

async function refreshOnce() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      if (!data.session?.access_token) throw new Error("No refreshed session");
      return data.session;
    })().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  let session = data?.session;
  if (!session) return null;
  const secondsLeft = (session.expires_at ?? 0) - Math.floor(Date.now() / 1000);
  if (secondsLeft <= PROACTIVE_REFRESH_SECONDS) {
    try { session = await refreshOnce(); } catch (_) { /* fall through to normal 401 recovery */ }
  }
  return session?.access_token ?? null;
}

async function signOutAndRedirect() {
  if (redirectingToLogin) return;
  if (window.location.pathname.startsWith("/login")) return;
  redirectingToLogin = true;
  try { await supabase.auth.signOut(); } catch (_) { /* noop */ }
  const next = window.location.pathname + window.location.search + window.location.hash;
  window.location.assign(`/login?next=${encodeURIComponent(next)}`);
}

api.interceptors.request.use(async (config) => {
  const token = await getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const viewAs = localStorage.getItem("tmic_view_as");
  if (viewAs) {
    config.headers["X-View-As"] = viewAs;
    const method = (config.method || "get").toLowerCase();
    if (!["get", "head", "options"].includes(method)) {
      toast.error("Read-only while viewing as another user. Exit View As to make changes.");
      return Promise.reject({ __viewAsBlocked: true, message: "Read-only while viewing as another user." });
    }
  }

  const orgId = localStorage.getItem("tmic_org");
  if (orgId) config.headers["X-Org-Id"] = orgId;
  return config;
});

// On a 401 (expired/invalid session), refresh once and retry the original request.
// If refresh fails, sign out and bounce to login (preserving where the user was) instead
// of leaving them stuck on an "Invalid or expired session" screen.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    if (status !== 401 || !original || original._retried) {
      return Promise.reject(error);
    }
    original._retried = true;
    try {
      const session = await refreshOnce();
      original.headers = original.headers || {};
      original.headers.Authorization = `Bearer ${session.access_token}`;
      return api(original);
    } catch (refreshError) {
      await signOutAndRedirect();
      return Promise.reject(refreshError);
    }
  }
);

// App-wide double-submit guard: collapse identical concurrent mutating requests
// (the exact signature of a double-tap) into a single in-flight request so no
// duplicate records are ever created. Distinct payloads / sequential taps are unaffected.
const _inflight = new Map();
const _key = (method, url, data) => `${method}:${url}:${typeof data === "string" ? data : JSON.stringify(data ?? "")}`;
["post", "put", "delete"].forEach((method) => {
  const orig = api[method].bind(api);
  api[method] = (url, ...args) => {
    const data = method === "delete" ? (args[0]?.data ?? "") : (args[0] ?? "");
    const key = _key(method, url, data);
    if (_inflight.has(key)) return _inflight.get(key);
    const p = orig(url, ...args).finally(() => _inflight.delete(key));
    _inflight.set(key, p);
    return p;
  };
});

export default api;
